import { Link } from "@inertiajs/react";
import { Trophy, Lock, CheckCircle2, Plus, Ticket, Medal, Crown } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  formatPrizeAmount, topInClassPrizeLabel, topInClassStandingLabel, topClassroomLabel, topClassroomStandingLabel,
} from "@/lib/prizes";
import DivisionLeaderList from "@/components/schools/rewards/DivisionLeaderList";

/**
 * Rewards and prizes with progress. Each row is itself a progress bar
 * that fills as the tracked total grows; at 100% it flips to unlocked.
 * Shown on dashboards, the classroom page, the parent page and public
 * donation pages.
 *
 * Defaults to school-reward copy. Pass `title`, `emptyDescription`, and
 * `unlockedSummary` to repurpose for classroom rewards or a student's prizes.
 *
 * Props:
 *   milestones:        [{ id, kind, threshold_amount, prize, unlocked, current_amount, progress_pct, award_style, entry_count }]
 *                      top_in_class rows also carry { competes_within, group_label, rank, group_size, gap_to_first, gap_to_qualify, leading, tied_for_first, finalized, won }
 *                      top_classroom rows carry one classroom's standing { division_label, division_rank, division_size,
 *                      gap_to_first, gap_to_qualify, leading, tied_for_first, competing, finalized, won } or, on
 *                      school-wide staff views, { divisions: [{ label, leaders, leader_amount }], finalized }
 *   manage_path:       optional — renders a 'Manage' button in the header
 *   title:             card title (default "School rewards")
 *   emptyDescription:  shown above the empty-state Add button
 *   unlockedSummary:   ({ unlocked, total, entries }) => string for the description line.
 *                      `total` counts only rewards that unlock (not draws or top student/classroom);
 *                      `entries` is the draw entries earned (also passed as `ballots` for older callers).
 */
export default function MilestonesCard({
  milestones,
  manage_path,
  title = "School rewards",
  emptyDescription = "Set school rewards to rally the whole school around shared goals.",
  unlockedSummary,
}) {
  if (!milestones?.length) {
    return manage_path ? (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {title}
          </CardTitle>
          <CardDescription>{emptyDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" asChild>
            <Link href={manage_path}>
              <Plus className="h-4 w-4" />
              Add rewards
            </Link>
          </Button>
        </CardContent>
      </Card>
    ) : null;
  }

  const unlockable = milestones.filter((m) => !m.award_style || m.award_style === "unlock");
  const unlockedCount = unlockable.filter((m) => m.unlocked).length;
  const entries = milestones.reduce((sum, m) => sum + entryCountFor(m), 0);
  const summary = unlockedSummary
    ? unlockedSummary({ unlocked: unlockedCount, total: unlockable.length, entries, ballots: entries })
    : unlockable.length > 0
    ? `${unlockedCount} of ${unlockable.length} unlocked. The whole school earns these together.`
    : "Classes compete for the top spot in their division.";

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {title}
          </CardTitle>
          <CardDescription className="mt-1">{summary}</CardDescription>
        </div>
        {manage_path && (
          <Button variant="outline" size="sm" asChild>
            <Link href={manage_path}>Manage</Link>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2.5">
        {milestones.map((m) => (
          <MilestoneRow key={m.id} milestone={m} />
        ))}
      </CardContent>
    </Card>
  );
}

function MilestoneRow({ milestone }) {
  if (milestone.award_style === "top_in_class") {
    return (
      <StandingRow
        milestone={milestone}
        icon={Medal}
        title={topInClassPrizeLabel(milestone)}
        standing={topInClassStandingLabel(milestone)}
      />
    );
  }
  if (milestone.award_style === "top_classroom") {
    if (milestone.divisions) return <DivisionLeadersRow milestone={milestone} />;
    return (
      <StandingRow
        milestone={milestone}
        icon={Crown}
        title={`${topClassroomLabel(milestone)}: ${milestone.prize}`}
        standing={topClassroomStandingLabel(milestone)}
      />
    );
  }

  const { unlocked, kind, threshold_amount, prize, current_amount, progress_pct } = milestone;
  const isDraw = milestone.award_style === "raffle";
  const entries = entryCountFor(milestone);
  const hasEntries = isDraw && entries > 0;
  const threshold = formatPrizeAmount(kind, threshold_amount);
  const current = formatPrizeAmount(kind, current_amount ?? 0);
  const pct = progress_pct ?? 0;

  return (
    <div
      className={`relative overflow-hidden rounded-lg border px-4 py-3 ${
        hasEntries ? "border-primary/35 bg-primary/5" : unlocked ? "border-success/40 bg-success/5" : "border-border bg-card"
      }`}
    >
      {/* Fill bar — sits behind the content as a subtle progress fill */}
      <div
        className={`absolute inset-y-0 left-0 transition-[width] duration-500 motion-reduce:transition-none ${
          hasEntries ? "bg-primary/10" : unlocked ? "bg-success/15" : "bg-primary/10"
        }`}
        style={{ width: `${pct}%` }}
        aria-hidden="true"
      />

      <div className="relative flex items-start gap-3">
        <div
          className={`mt-0.5 h-9 w-9 rounded-full border-2 flex items-center justify-center shrink-0 ${
            hasEntries
              ? "bg-primary border-primary text-primary-foreground"
              : unlocked
              ? "bg-success border-success text-success-foreground"
              : "border-border bg-card text-muted-foreground"
          }`}
        >
          {isDraw ? (
            <Ticket className="h-4 w-4" strokeWidth={hasEntries ? 3 : 2} />
          ) : unlocked ? (
            <CheckCircle2 className="h-4 w-4" strokeWidth={3} />
          ) : (
            <Lock className="h-4 w-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          {/* The prize gets the whole line and wraps; progress sits below
              so long names read cleanly on a phone. */}
          <p className="font-medium break-words text-foreground">
            {hasEntries ? `${entries.toLocaleString("en-CA")} ${entries === 1 ? "entry" : "entries"} in the ${drawName(prize)}` : prize}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-baseline gap-x-2">
            <span className="font-tabular">
              {isDraw ? `${current} so far · one entry per ${threshold}` : `${current} of ${threshold}`}
            </span>
            {!hasEntries && (
              <>
                <span className="opacity-60" aria-hidden="true">·</span>
                {isDraw ? (
                  <span>{pct}% of the way to a first entry</span>
                ) : unlocked ? (
                  <span className="font-semibold text-success">Unlocked</span>
                ) : (
                  <span>{pct}% of the way</span>
                )}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// A student's own standing for a top-student prize, or a classroom's
// for a top-classroom prize — rank and gap only, never anyone else's name.
function StandingRow({ milestone, icon: Icon, title, standing }) {
  const { won, leading, progress_pct } = milestone;
  const highlighted = won || leading;

  return (
    <div
      className={`relative overflow-hidden rounded-lg border px-4 py-3 ${
        won ? "border-success/40 bg-success/5" : leading ? "border-primary/35 bg-primary/5" : "border-border bg-card"
      }`}
    >
      <div
        className={`absolute inset-y-0 left-0 transition-[width] duration-500 motion-reduce:transition-none ${won ? "bg-success/15" : "bg-primary/10"}`}
        style={{ width: `${progress_pct ?? 0}%` }}
        aria-hidden="true"
      />
      <div className="relative flex items-start gap-3">
        <div
          className={`mt-0.5 h-9 w-9 rounded-full border-2 flex items-center justify-center shrink-0 ${
            won
              ? "bg-success border-success text-success-foreground"
              : leading
              ? "bg-primary border-primary text-primary-foreground"
              : "border-border bg-card text-muted-foreground"
          }`}
        >
          {won ? <CheckCircle2 className="h-4 w-4" strokeWidth={3} /> : <Icon className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium break-words text-foreground">{title}</p>
          <p className={`text-xs mt-0.5 ${won ? "font-semibold text-success" : highlighted ? "text-primary font-medium" : "text-muted-foreground"}`}>
            {standing}
          </p>
        </div>
      </div>
    </div>
  );
}

// School-wide staff view of a top-classroom prize: who leads (or won)
// each division.
function DivisionLeadersRow({ milestone }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-9 w-9 rounded-full border-2 border-border bg-card text-muted-foreground flex items-center justify-center shrink-0">
          <Crown className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium break-words text-foreground">Top classroom: {milestone.prize}</p>
          <DivisionLeaderList prize={milestone} className="mt-0.5" />
        </div>
      </div>
    </div>
  );
}

// "Bluetooth speaker draw" stays as it is; "iPad" becomes "iPad draw".
function drawName(prize) {
  return /\bdraw$/i.test(prize.trim()) ? prize : `${prize} draw`;
}

// Entries a student has in a prize draw: the stored count, or what their
// total already earns if the stored count hasn't caught up.
function entryCountFor(milestone) {
  if (milestone.award_style !== "raffle") return 0;

  const stored = Number(milestone.entry_count ?? 0);
  const threshold = Number(milestone.threshold_amount ?? 0);
  const current = Number(milestone.current_amount ?? 0);
  const computed = threshold > 0 ? Math.floor(current / threshold) : 0;

  return Math.max(stored, computed);
}
